import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { API_BASE_URL, EXTREME_B2B_API_BASE_URL } from '../config';
import { AUTH_UNAUTHORIZED_EVENT } from '../utils/authBus';
import { broadcastLogout, onLogoutBroadcast, attemptCloseTab } from '../utils/crossTabLogout';
import { consumeExternalReturnUrl } from '../utils/externalSession';
import {
  oauthKey,
  settingsKey,
  apiKeyStorageKey,
  readStoredApiKey,
  getActiveAccountId,
  bindActiveAccountId,
  clearActiveAccountId,
} from '../utils/accountSession';

const LOGOUT_SCOPE = 'ai-tech-main';

// Reads this tab's bound OAuth session, if any — namespaced by account id so
// two tabs on two different accounts never read each other's credentials.
function readOAuthSession() {
  const accountId = getActiveAccountId();
  if (!accountId) return null;
  const raw = localStorage.getItem(oauthKey(accountId));
  if (!raw) return null;
  try {
    const tokenData = JSON.parse(unescape(atob(raw)));
    return tokenData?.accessToken ? { accountId, tokenData } : null;
  } catch (e) {
    return null;
  }
}

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [apiKey, setApiKey] = useState('');
  const [account, setAccount] = useState(null);
  const [redirectPath, setRedirectPath] = useState('error');
  const [reconnecting, setReconnecting] = useState(false);
  const retryIntervalRef = useRef(null);
  const isReconnectingRef = useRef(false);
  const navigate = useNavigate();

  // ── Reconnect helpers ────────────────────────────────────────────────────────

  const stopReconnect = useCallback(() => {
    if (retryIntervalRef.current) {
      clearInterval(retryIntervalRef.current);
      retryIntervalRef.current = null;
    }
    isReconnectingRef.current = false;
    setReconnecting(false);
  }, []);

  const tryRevalidate = useCallback(async () => {
    const storedKey = readStoredApiKey();
    const oauthSession = readOAuthSession();

    let headers = null;
    if (storedKey) {
      headers = { 'X-API-Key': storedKey };
    } else if (oauthSession) {
      // The backend's token-auth path requires accountId + appInstanceId
      // headers alongside the bearer token (get_header_values() 400s
      // without them) — omitting them meant this health-check could never
      // succeed for OAuth sessions, so the reconnect loop never stopped and
      // the "trying to renew authorization" banner got stuck forever.
      const { tokenData } = oauthSession;
      headers = {
        Authorization: `Bearer ${tokenData.accessToken}`,
        ...(tokenData.accountId != null && { accountId: String(tokenData.accountId) }),
        ...(tokenData.appInstanceId != null && { appInstanceId: String(tokenData.appInstanceId) }),
        ...(tokenData.accountUserId != null && { accountUserId: String(tokenData.accountUserId) }),
      };
    }

    if (!headers) {
      stopReconnect();
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/health/detailed`, { headers });
      if (response.ok) {
        const data = await response.json();
        stopReconnect();
        setIsAuthenticated(true);
        if (storedKey) {
          setApiKey(storedKey);
          setAccount({
            id: data.account_id || 0,
            name: data.account_name || `Account ${data.account_id || ''}`,
            api_key: storedKey,
            logo_url: data.account_logo_url || data.logo_url || '',
          });
        }
        toast.success('Connection restored!');
      }
    } catch (e) {
      // keep retrying
    }
  }, [stopReconnect]);

  const startReconnect = useCallback(() => {
    if (isReconnectingRef.current) return;
    isReconnectingRef.current = true;
    setReconnecting(true);
    tryRevalidate();
    retryIntervalRef.current = setInterval(tryRevalidate, 10000);
  }, [tryRevalidate]);

  // Keep a stable ref to startReconnect so effects with empty deps can call it.
  const startReconnectRef = useRef(null);
  startReconnectRef.current = startReconnect;

  // Listen for 401 events fired by service-layer interceptors.
  useEffect(() => {
    const handler = () => {
      const storedKey = readStoredApiKey();
      if (storedKey || readOAuthSession()) {
        startReconnectRef.current();
      }
    };
    window.addEventListener(AUTH_UNAUTHORIZED_EVENT, handler);
    return () => {
      window.removeEventListener(AUTH_UNAUTHORIZED_EVENT, handler);
      stopReconnect();
    };
  }, [stopReconnect]);

  // ── Startup auth check ───────────────────────────────────────────────────────

  // Check if user is authenticated on mount (API key or OAuth token from agentic_ai_auth)
  useEffect(() => {
    const storedApiKey = readStoredApiKey();
    if (storedApiKey) {
      setApiKey(storedApiKey);
      validateApiKey(storedApiKey);
      return;
    }
    const oauthSession = readOAuthSession();
    if (oauthSession) {
      const { accountId, tokenData } = oauthSession;
      setApiKey(tokenData.accessToken);
      setIsAuthenticated(true);
      let appSettings = {};
      try {
        const appSettingsStr = localStorage.getItem(settingsKey(accountId));
        appSettings = appSettingsStr ? JSON.parse(appSettingsStr) : {};
      } catch (e) {
        console.error('Failed to parse stored App-Settings', e);
      }
      setAccount({
        id: tokenData.appInstanceId,
        name: tokenData?.businessName || `App ${tokenData.appInstanceId || ''}`,
        username: tokenData?.username || '',
        api_key: tokenData.accessToken,
        logo_url: appSettings?.logoUrl || '',
        accountId: tokenData.accountId,
        appInstanceId: tokenData.appInstanceId,
        accountUserId: tokenData.accountUserId,
      });
    }
    setLoading(false);
  }, []);

  // Validate API key with backend
  const validateApiKey = async (key) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/health/detailed`, {
        headers: {
          'X-API-Key': key,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setApiKey(key);
        setIsAuthenticated(true);
        setAccount({
          id: data.account_id || 0,
          name: data.account_name || `Account ${data.account_id || ''}`,
          api_key: key,
          logo_url: data.account_logo_url || data.logo_url || '',
        });
        setLoading(false);
      } else {
        // Server reachable but key rejected — could be a stale session after restart.
        // Trigger reconnect loop rather than immediately wiping credentials.
        setLoading(false);
        startReconnectRef.current();
      }
    } catch (error) {
      // Network/server unreachable — backend may be restarting.
      console.error('Error validating API key:', error);
      setLoading(false);
      startReconnectRef.current();
    }
  };

  // Login function
  const login = async (key, options = {}) => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/v1/health/detailed`, {
        headers: {
          'X-API-Key': key,
        },
      });

      if (response.ok) {
        const data = await response.json();
        // Bind this tab to the account the key belongs to, and namespace the
        // stored key by it — same convention as the OAuth flow — so a second
        // account logging in via API key in another tab never overwrites it.
        bindActiveAccountId(data.account_id);
        localStorage.setItem(apiKeyStorageKey(data.account_id), key);
        setApiKey(key);
        setIsAuthenticated(true);
        setAccount({
          id: data.account_id || 0,
          name: data.account_name || `Account ${data.account_id || ''}`,
          api_key: key,
          logo_url: data.account_logo_url || data.logo_url || '',
        });
        toast.success('Login successful!');
        const redirectTo = String(options?.redirectTo || '/dashboard').trim() || '/dashboard';
        navigate(redirectTo);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Invalid API key');
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.message || 'Login failed');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Clears every piece of session state this auth flow writes to storage —
  // both the credentials themselves and the data fetched using them
  // (account settings, locale strings, permissions), so none of it leaks to
  // the next session on a shared device. Only removes THIS tab's bound
  // account's namespaced keys — other tabs running other accounts are
  // untouched.
  const clearStoredSession = (accountId) => {
    // Legacy flat key removal, plus this account's own namespaced entries.
    localStorage.removeItem('ai_tech_api_key');
    if (accountId) {
      localStorage.removeItem(oauthKey(accountId));
      localStorage.removeItem(settingsKey(accountId));
      localStorage.removeItem(apiKeyStorageKey(accountId));
    }
    clearActiveAccountId();
    sessionStorage.removeItem('USER-VALIDATE');
    sessionStorage.removeItem('LanguageData');
    sessionStorage.removeItem('PERMISSIONS');
  };

  // Logout function
  const logout = useCallback(() => {
    // Only send the user back to a captured external returnUrl/referrer.
    // With no explicit one on hand — whether this was an OAuth /validate
    // session or a direct API-key login — logout lands on our own
    // logged-out page rather than guessing at the external platform's URL.
    const activeAccountId = getActiveAccountId();
    const externalReturnUrl = consumeExternalReturnUrl();
    clearStoredSession(activeAccountId);
    // Scoped by account id so logging out one account's tabs never signals
    // a different account's tabs in the same browser.
    const scope = activeAccountId ? `${LOGOUT_SCOPE}:${activeAccountId}` : LOGOUT_SCOPE;
    broadcastLogout(scope, externalReturnUrl);
    // Hard navigation, and deliberately NOT calling setIsAuthenticated(false)
    // etc. first: this tab's whole React tree is about to be torn down by
    // the navigation anyway, and flipping isAuthenticated while still on a
    // ProtectedRoute-guarded page makes it render <Navigate to="/login"/>
    // for a render or two — a visible flash of /login — before the browser
    // finishes navigating away. Skipping the state update avoids that flash
    // entirely; the token is already cleared from localStorage above, which
    // is what actually matters for security.
    window.location.href = externalReturnUrl || '/logged-out';
  }, []);

  // Other tabs on the SAME account: clear session, then either return to the
  // external app that launched this session, or show the logged-out page.
  // Scoped to account.accountId so this re-subscribes to the right channel
  // once an OAuth session resolves (mount-restored or freshly validated);
  // plain-API-key tabs stay on the unscoped channel, matching today's
  // single-shared-session behavior for that flow.
  useEffect(() => {
    const activeAccountId = account?.accountId || getActiveAccountId();
    const scope = activeAccountId ? `${LOGOUT_SCOPE}:${activeAccountId}` : LOGOUT_SCOPE;
    const unsubscribe = onLogoutBroadcast(scope, (externalReturnUrl) => {
      clearStoredSession(activeAccountId);
      attemptCloseTab();
      window.location.href = externalReturnUrl || '/logged-out';
    });
    return unsubscribe;
  }, [account?.accountId]);

  // Update API key
  const updateApiKey = async (newKey) => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/v1/health/detailed`, {
        headers: {
          'X-API-Key': newKey,
        },
      });

      if (response.ok) {
        const data = await response.json();
        bindActiveAccountId(data.account_id);
        localStorage.setItem(apiKeyStorageKey(data.account_id), newKey);
        setApiKey(newKey);
        setIsAuthenticated(true);
        setAccount({
          id: data.account_id || 0,
          name: data.account_name || `Account ${data.account_id || ''}`,
          api_key: newKey
        });
        toast.success('API key updated successfully!');
        return true;
      } else {
        throw new Error('Invalid API key');
      }
    } catch (error) {
      console.error('Error updating API key:', error);
      toast.error('Failed to update API key');
      return false;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Get account detail by key (matches Vue getAccDetail for interceptor compatibility).
   * Returns accountId, appInstanceId, accountUserId from OAuth token when available.
   */
  const getAccDetail = (key) => {
    if (account?.[key] != null) return account[key];
    const oauthSession = readOAuthSession();
    return oauthSession?.tokenData?.[key] ?? '';
  };

  // Get authenticated headers for API calls (interceptor-style for selected APIs)
  const getAuthHeaders = useCallback((options = {}) => {
    const { useBearer = true } = options;
    const oauthSession = readOAuthSession();

    if (oauthSession) {
      const { tokenData } = oauthSession;
      const token = tokenData?.accessToken;
      const accId = account?.accountId ?? tokenData?.accountId ?? '';
      const appId = account?.appInstanceId ?? tokenData?.appInstanceId ?? '';
      const userId = account?.accountUserId ?? tokenData?.accountUserId ?? '';
      if (token) {
        const headers = {
          'Content-Type': 'application/json',
          ...(useBearer && { Authorization: `Bearer ${token}` }),
          ...(accId !== '' && { accountId: String(accId) }),
          ...(appId !== '' && { appInstanceId: String(appId) }),
          ...(userId !== '' && { accountUserId: String(userId) }),
        };
        return headers;
      }
    }

    const storedApiKey = readStoredApiKey();
    const keyToUse = apiKey || storedApiKey;
    if (!keyToUse) {
      console.error('No API key available in getAuthHeaders');
      logout();
      return {};
    }
    return {
      'X-API-Key': keyToUse,
      'Content-Type': 'application/json',
    };
  }, [account, apiKey, logout]);

  /**
   * Authenticate using OAuth callback code (Vue validate flow).
   * Fetches redirect URL, exchanges code for token, loads app settings and optional UI/permissions.
   */
  const authenticateWithCode = async (code) => {
    if (!code) {
      setLoading(false);
      return { ok: false, reason: 'missing_code' };
    }

    try {
      const redirectRes = await fetch(
        `${EXTREME_B2B_API_BASE_URL}/manage/auth/getredirecturl?redirectUrlName=ERROR_PAGE_REDIRECT`
      );
      const redirectData = await redirectRes.json();
      const errorRedirectUrl = redirectData?.redirectUrl || 'error';
      setRedirectPath(errorRedirectUrl);

      const tokenRes = await fetch(
        `${EXTREME_B2B_API_BASE_URL}/manage/auth/getappproperity?code=${encodeURIComponent(code)}`
      );
      const tokenData = await tokenRes.json();

      if (!tokenData?.accessToken) {
        setLoading(false);
        return { ok: false, reason: 'invalid_code' };
      }

      // Bind THIS tab to this account (sessionStorage, per-tab) and store
      // its credentials under an account-namespaced localStorage key, so a
      // different account logging in from another tab never overwrites it.
      bindActiveAccountId(tokenData.accountId);
      localStorage.setItem(oauthKey(tokenData.accountId), btoa(unescape(JSON.stringify(tokenData))));
      setApiKey(tokenData.accessToken);
      setIsAuthenticated(true);

      const headers = getAuthHeaders();

      // The token can decode fine on the external platform and still have
      // no matching account on our side (e.g. never provisioned here) — the
      // whole session is then unusable. Fail cleanly now with a reason the
      // UI can show a friendly message for, instead of letting the user
      // into a broken dashboard where every API call 401s individually.
      const abandonSession = () => {
        localStorage.removeItem(oauthKey(tokenData.accountId));
        localStorage.removeItem(settingsKey(tokenData.accountId));
        clearActiveAccountId();
        setApiKey('');
        setIsAuthenticated(false);
        setAccount(null);
      };
      try {
        const accountCheckRes = await fetch(`${API_BASE_URL}/api/v1/health/detailed`, { headers });
        if (!accountCheckRes.ok) {
          abandonSession();
          setLoading(false);
          return { ok: false, reason: 'account_not_found' };
        }
      } catch (e) {
        abandonSession();
        setLoading(false);
        return { ok: false, reason: 'network_error' };
      }

      const settingsRes = await fetch(
        `${EXTREME_B2B_API_BASE_URL}/developerApp/accountAppInstance/settings/${tokenData.appInstanceId}`,
        { headers }
      );
      const appSettings = await settingsRes.json();
      sessionStorage.setItem('USER-VALIDATE', 'VALID');
      localStorage.setItem(settingsKey(tokenData.accountId), JSON.stringify(appSettings));

      setAccount({
        id: tokenData.appInstanceId,
        name: appSettings?.name || `App ${tokenData.appInstanceId || ''}`,
        username: tokenData?.username || '',
        api_key: tokenData.accessToken,
        logo_url: appSettings?.logoUrl || '',
        accountId: tokenData.accountId,
        appInstanceId: tokenData.appInstanceId,
        accountUserId: tokenData.accountUserId,
      });

      // Optional: fetch UI properties and permissions (parallel)
      try {
        const appName = appSettings?.name || '';
        const uiPropsUrl = `${EXTREME_B2B_API_BASE_URL}/developerApp/properties/app-ui-properties?appInstanceId=${tokenData.appInstanceId}`;
        const permUrl = `${EXTREME_B2B_API_BASE_URL}/developerApp/userRolesPermissionList?accountUserId=${tokenData.accountUserId}&appName=${appName}&accountId=${tokenData.accountId}&appInstanceId=${tokenData.appInstanceId}`;
       
        const [uiRes, permRes] = await Promise.all([
          fetch(uiPropsUrl, { headers }),
          fetch(permUrl, { headers }),
        ]);
        const uiJson = await uiRes.json();
        const permJson = await permRes.json();
        const uiPropValues = Array.isArray(uiJson) ? uiJson : uiJson?.content || [];
        const enJson = {};
        const frJson = {};
        const spJson = {};
        uiPropValues.forEach((uiProp) => {
          if (uiProp?.propertyName && uiProp?.locale && Object.keys(uiProp.locale).length) {
            const { en, fr, sp } = uiProp.locale;
            enJson[uiProp.propertyName] = (en?.trim() !== '' ? en?.trim() : uiProp.defaultValue?.trim()) || '';
            frJson[uiProp.propertyName] = (fr?.trim() !== '' ? fr?.trim() : en?.trim()) || '';
            spJson[uiProp.propertyName] = (sp?.trim() !== '' ? sp?.trim() : en?.trim()) || '';
          }
        });
        sessionStorage.setItem('LanguageData', JSON.stringify({ en: enJson, fr: frJson, sp: spJson }));
        const permissionsList = permJson?.appUserRolesPermissions || {};
        const basicInfo = ['View_Assembly_Diagram_Group', 'View_Assembly_Diagrams', 'View_Manufacturer', 'View_Brand', 'View_Assets'];
        const localMapPermissions = Object.keys(permissionsList).length > 0
          ? Object.keys(permissionsList) // or map through appPermissions if you have that module
          : basicInfo;
        sessionStorage.setItem('PERMISSIONS', JSON.stringify(localMapPermissions));
      } catch (e) {
        console.error('UI properties/permissions fetch failed:', e);
      }
      return { ok: true };
    } catch (e) {
      console.error('authenticateWithCode error:', e);
      return { ok: false, reason: 'exchange_failed' };
    } finally {
      setLoading(false);
    }
  };

  const getReturnUrl = () => redirectPath;

  // Check if the current session (OAuth or API key) is still valid
  const checkAuthStatus = async () => {
    if (!apiKey) {
      return false;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/health/detailed`, {
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        return true;
      } else {
        // Invalid API key, logout user
        logout();
        return false;
      }
    } catch (error) {
      console.error('Error checking auth status:', error);
      logout();
      return false;
    }
  };

  const value = {
    isAuthenticated,
    loading,
    apiKey,
    account,
    redirectPath,
    reconnecting,
    login,
    logout,
    updateApiKey,
    getAuthHeaders,
    getAccDetail,
    checkAuthStatus,
    authenticateWithCode,
    getReturnUrl,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
